import type {
  OrganisationDto,
  OrganisationMemberDto,
  OrganisationRole,
  UserOrganisation,
} from "../../types/organisation";

const ORG_ROLES = new Set<OrganisationRole>([
  "org_admin",
  "form_manager",
  "form_viewer",
]);

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function readId(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

function readOrgRole(value: unknown): OrganisationRole | null {
  return typeof value === "string" && ORG_ROLES.has(value as OrganisationRole)
    ? (value as OrganisationRole)
    : null;
}

export function normalizeUserOrganisation(
  raw: unknown,
): UserOrganisation | null {
  if (!raw || typeof raw !== "object") return null;

  const item = raw as Record<string, unknown>;
  const id = readId(item.id);
  const name = readString(item.name);
  const slug = readString(item.slug);
  const role = readOrgRole(item.role);

  if (id == null || !name || !slug || !role) return null;

  return { id, name, slug, role };
}

export function normalizeUserOrganisations(raw: unknown): UserOrganisation[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(normalizeUserOrganisation)
    .filter((item): item is UserOrganisation => item != null);
}

export function normalizeOrganisation(raw: unknown): OrganisationDto | null {
  if (!raw || typeof raw !== "object") return null;

  const item = raw as Record<string, unknown>;
  const id = readId(item.id);
  const name = readString(item.name);
  const slug = readString(item.slug);

  if (id == null || !name || !slug) return null;

  const isActiveRaw = item.isActive ?? item.is_active;

  return {
    id,
    name,
    slug,
    isActive: typeof isActiveRaw === "boolean" ? isActiveRaw : undefined,
    createdAt: readString(item.createdAt ?? item.created_at) ?? undefined,
    updatedAt: readString(item.updatedAt ?? item.updated_at) ?? undefined,
  };
}

export function normalizeOrganisationsList(raw: unknown): OrganisationDto[] {
  if (Array.isArray(raw)) {
    return raw
      .map(normalizeOrganisation)
      .filter((item): item is OrganisationDto => item != null);
  }

  if (raw && typeof raw === "object" && "data" in raw) {
    return normalizeOrganisationsList((raw as { data: unknown }).data);
  }

  return [];
}

export function normalizeOrganisationMember(
  raw: unknown,
): OrganisationMemberDto | null {
  if (!raw || typeof raw !== "object") return null;

  const item = raw as Record<string, unknown>;
  const userId = readId(item.userId ?? item.user_id);
  const role = readOrgRole(item.role);

  if (userId == null || !role) return null;

  return {
    userId,
    name: readString(item.name) ?? undefined,
    email: readString(item.email) ?? undefined,
    role,
  };
}

export function normalizeOrganisationMembers(
  raw: unknown,
): OrganisationMemberDto[] {
  if (Array.isArray(raw)) {
    return raw
      .map(normalizeOrganisationMember)
      .filter((item): item is OrganisationMemberDto => item != null);
  }

  if (raw && typeof raw === "object" && "data" in raw) {
    return normalizeOrganisationMembers((raw as { data: unknown }).data);
  }

  return [];
}
